import { useState, useMemo, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { Divider, IconButton, Grid, FormHelperText } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useLoanNumber } from "../../fiscalUpgrade/CustomHooks/useLoanNumber";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import CustomAutocomplete from "../../../shared/AutoComplete";
import CustomTextField from "../../../shared/CustomTextFeild";
import ContainedButton from "../../../shared/Button/CustomContainedButton";
import OutlinedButton from "../../../shared/Button/customOutlinedButton";
import environment from "../../../environments/environment";
import { axiosInterceptor } from "../../fiscalUpgrade/services/authInterceptor";
import * as constants from "../../../constants";
import { notifyUser } from "../../fiscalUpgrade/Utils/notifyUser";

type PopUpProps = {
  open: boolean;
  handleClose: () => void;
};

export default function LoanStatementReport(props: PopUpProps) {
  const { open, handleClose } = props;
  const handleDialogClose = () => {
    handleClose(); // Close the dialog
    resetAll();
  };
  const [loanName, setLoanName] = useState(null);
  const GET_DATES = environment.postNewLoan;
  const { t } = useTranslation();
  const translate = t as (key: string) => string;
  const User = useSelector((state: RootState) => state.auth);
  const [disabled, setDisabled] = useState(true);
  const userInfo = useSelector((state: RootState) => state.auth);
  const [montlyEndDateList, setMontlyEndDateList] = useState([]);
  const [monthEndDate, setMonthEndDate] = useState<string | null>("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [loanId, setLoanId] = useState("");
  const [loanMessage, setLoanMessage] = useState<string | null>(null);
  const [monthErr, setMonthErr] = useState<string | null>(null);
  const participantDetail = useSelector(
    (state: RootState) => state.participantDetails
  );
  const { loanNumber, getLoanNumber, setLoanNumber } = useLoanNumber();
  useEffect(() => {
    if (open) {
      getLoanNumber(participantDetail.participantId, User.clientId);
    }
  }, [open, participantDetail.participantId, User.clientId]);
  function validateDetails(): boolean {
  let valid = true;

  if (!loanName) {
    setLoanMessage(translate("pleaseSelectLoan"));
    valid = false;
  } else {
    setLoanMessage(null);
  }

  if (!monthEndDate) {
    setMonthErr(translate("pleaseSelectMonth"));
    valid = false;
  } else {
    setMonthErr(null);
  }

  return valid;
}

  function resetAll() {
    setLoanNumber([]);
    setMonthEndDate("");
    setDisabled(true);
    setLoanName(null);
    setLoanId("");
    setYear("");
    setMonth("");
    setLoanMessage(null)
    setMonthErr(null)
  }
  async function getPreviousMonths(id: any) {
    if (!id) {
      return;
    }
    try {
      const response = await axiosInterceptor.get(
        `${GET_DATES}${id}/statement-dates?clientId=${userInfo.clientId}`
      );

      if (response && response.status === constants.STATUS_CODES.SUCCESS) {
        const formatedList = response?.data?.data?.map((dateStr: any) => {
          const date = new Date(dateStr);
          const formatted = date.toLocaleDateString("en-US", {
            month: "short",
            year: "numeric",
          });
          return {
            value: dateStr,
            label: formatted,
          };
        });
        setLoanId(id);
        setMontlyEndDateList(formatedList);
      }
    } catch (error: any) {
      notifyUser(
        error.response?.data?.message ?? translate("apiRequestFailed"),
        "error",
        translate("loanStatement")
      );
    }
  }
  function handleMonthChange(data: any) {
    setMonthEndDate(data);
    const date = new Date(data);
    setYear(date.getFullYear().toString());
    setMonth(String(date.getMonth() + 1).padStart(2, "0"));
    setMonthErr(null)

  }
  function handleLoanNumber(data: any) {
    if (data) {
      setLoanName(data?.loanNumber);
      setDisabled(false);
      getPreviousMonths(data?.loanId);
     setLoanMessage(null);
     setMonthErr(null)
    } else {
      setMonthEndDate("");
      setLoanName(null);
      setDisabled(true); // Optional: disable other controls if needed
    }
  }
  async function handleDownload() {
   
    if (validateDetails()) {
      try {
        const response = await axiosInterceptor.get(
          `${GET_DATES}${loanId}/generate-statement?month=${month}&year=${year}&isEmailRequired=true&clientId=${userInfo.clientId}`
        );
        if (response && response.status === constants.STATUS_CODES.SUCCESS) {
          if (response?.data?.data?.fileContent) {
            const base64String = response?.data?.data?.fileContent; // Ensure this is the actual base64 string
            const byteCharacters = atob(base64String);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: "application/pdf" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = "loan-statement-report.pdf";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            resetAll();
            handleClose();
            return;
          }
        }
      } catch (error: any) {
        handleClose();
         resetAll() 
        
        notifyUser(
          error.response?.data?.message ?? translate("apiRequestFailed"),
          "error",
          translate("loanStatement")
        );
      }
    }
  }
  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={false}
      sx={{
        maxWidth: "700px", // Adjust width as needed
        margin: "auto", // Center horizontally
        display: "flex", // Ensure flex behavior
        alignItems: "center", // Center vertically
        justifyContent: "center", // Center horizontally
        borderRadius: 2,
        "& .MuiPaper-root": { borderRadius: "12px", background: "white" },
      }}
    >
      {/* Dialog Header */}
      <DialogTitle
        sx={{
          backgroundColor: "#059ca9",
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          padding: "10px 10px",
        }}
      >
        {translate("loanStatements")}
        <IconButton
          onClick={handleDialogClose}
          sx={{ color: "white", padding: "0px" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Dialog Content */}
      <DialogContent style={{ paddingTop: "20px" }}>
        <Grid container columnSpacing={2}>
          {/* Type Dropdown */}
          <Grid item xs={6}>
            <CustomAutocomplete
              labelText={translate("loan")}
              placeholder={translate("select")}
              size="small"
              options={loanNumber}
              getOptionLabel={(option: any) =>
                `${option?.loanNumber} - ${option?.description}` || ""
              }
              value={
                loanNumber?.find((option) => option.loanNumber === loanName) ||
                null
              }
              onChange={(e, value) => handleLoanNumber(value)}
              slotProps={{
                paper: {
                  sx: { backgroundColor: "white !important" },
                },
              }}
              error={Boolean(loanMessage)}
            />
            {loanMessage && (
              <FormHelperText style={{ color: "#d32f2f", fontSize: "15px" }}>
                {loanMessage}
              </FormHelperText>
            )}
          </Grid>
          <Grid item xs={6}>
            <CustomAutocomplete
              sx={{ width: "250px" }}
              labelText={translate("clientMonth")}
              readOnly={disabled}
              options={montlyEndDateList}
              getOptionLabel={(option) => option.label || ""}
              value={
                montlyEndDateList.find((d: any) => d.value === monthEndDate) ||
                null
              }
              onChange={(event, newValue) => {
                handleMonthChange(newValue?.value ?? "");
              }}
              slotProps={{
                paper: {
                  sx: { backgroundColor: "white !important" },
                },
              }}
              placeholder={translate("select")}
              error={Boolean(monthErr)}
            />
             {monthErr && (
              <FormHelperText style={{ color: "#d32f2f", fontSize: "15px" }}>
                {monthErr}
              </FormHelperText>
            )}
          </Grid>
          <Grid item xs={6} mt={2}>
            <CustomTextField
              labelText={translate("format")}
              readOnly
              value={translate("printablePdfFormat")}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <Divider />

      {/* Dialog Actions */}
      <DialogActions>
        <ContainedButton onClick={handleDownload}>
          {translate("download")}
        </ContainedButton>
        <OutlinedButton onClick={handleDialogClose}>
          {translate("cancel")}
        </OutlinedButton>
      </DialogActions>
    </Dialog>
  );
}
